import React, { useState, useEffect, useCallback } from 'react';
import { client } from '../utils/api';

const CONTROLLER = 'eventRegistrationController.php';
const EVENTS_CONTROLLER = 'eventsController.php';
const USERS_CONTROLLER = 'usersController.php';
const BASE_URL = process.env.REACT_APP_DB_BASE_URL || 'http://localhost/api';
const formatCHF = (amount) => new Intl.NumberFormat('en-CH', {
  style: 'currency',
  currency: 'CHF',
}).format(Number(amount) || 0);

export default function EventRegistrationsManager({ onSendEmail = () => {} }) {
  const [events, setEvents] = useState([]);
  const [collectedByEvent, setCollectedByEvent] = useState({});
  const [attendeeCountByEvent, setAttendeeCountByEvent] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [amountPaidToAdd, setAmountPaidToAdd] = useState('');
  const [amountPreset, setAmountPreset] = useState('custom');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadEvents = useCallback(async () => {
    try {
      const res = await client.get(EVENTS_CONTROLLER);
      setEvents(res.events || []);
      if (!selectedEventId && (res.events || []).length) {
        setSelectedEventId(String(res.events[0].id));
      }
    } catch (err) {
      setError('Failed to fetch events: ' + err.message);
    }
  }, [selectedEventId]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await client.get(USERS_CONTROLLER);
      setUsers(res.users || []);
    } catch (err) {
      setError((previous) => previous || 'Failed to fetch users: ' + err.message);
    }
  }, []);

  const loadCollectedAmounts = useCallback(async () => {
    try {
      const result = await client.get(CONTROLLER);
      const totals = {};
      const attendeeCounts = {};
      (result.registrations || []).forEach((registration) => {
        const eventId = registration.eventId ?? registration.event_id;
        const amountPaid = Number(registration.amountPaid ?? registration.amount_paid ?? 0);
        if (eventId === undefined || eventId === null) {
          return;
        }

        const eventKey = String(eventId);
        const status = String(registration.registration_status || registration.status || '').toLowerCase();
        if (status !== 'cancelled' && status !== 'removed') {
          attendeeCounts[eventKey] = (attendeeCounts[eventKey] || 0) + 1;
        }
        if (Number.isFinite(amountPaid)) {
          totals[eventKey] = (totals[eventKey] || 0) + amountPaid;
        }
      });
      setCollectedByEvent(totals);
      setAttendeeCountByEvent(attendeeCounts);
    } catch (err) {
      setError((previous) => previous || 'Failed to fetch collected amounts: ' + err.message);
    }
  }, []);

  const getEventBreakeven = (event) => {
    const costs = Array.isArray(event?.costs)
      ? event.costs
      : Array.isArray(event?.eventCosts)
        ? event.eventCosts
        : [];
    const attendeeCount = attendeeCountByEvent[String(event.id)] || 0;

    const totalCosts = costs.reduce((total, cost) => {
      const amount = Number(cost?.unitAmountChf ?? cost?.unit_amount_chf ?? cost?.amount ?? 0);
      const normalizedAmount = Number.isFinite(amount) ? amount : 0;
      const isVariable = String(cost?.costType || cost?.type || 'fixed').toLowerCase() === 'variable';
      return total + (isVariable ? normalizedAmount * attendeeCount : normalizedAmount);
    }, 0);

    return Number(collectedByEvent[String(event.id)] || 0) - totalCosts;
  };

  const loadRegistrationsForEvent = useCallback(async (eventId) => {
    try {
      const response = await fetch(`${BASE_URL}/${CONTROLLER}?eventId=${eventId}`, {
        method: 'GET', // or POST depending on your endpoint
        credentials: 'include' // MANDATORY to attach the PHPSESSID cookie
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      setRegistrations(result.registrations || []);
    } catch (err) {
      setError('Failed to fetch registrations: ' + err.message);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadUsers();
    loadCollectedAmounts();
  }, [loadEvents, loadUsers, loadCollectedAmounts]);

  useEffect(() => {
    if (!selectedEventId) {
      setRegistrations([]);
      return;
    }

    loadRegistrationsForEvent(selectedEventId);
  }, [selectedEventId, loadRegistrationsForEvent]);

  const selectedEvent = events.find((event) => String(event.id) === String(selectedEventId)) || null;

  const availableUsersForEvent = users.filter((user) => {
    const isAlreadyRegistered = registrations.some(
      (registration) => Number(registration.user_id) === Number(user.id)
    );

    if (isAlreadyRegistered) {
      return false;
    }

    const query = userSearchTerm.trim().toLowerCase();
    if (!query) {
      return false;
    }

    const searchableText = `${user.fullName || ''} ${user.email || ''}`.toLowerCase();
    return searchableText.includes(query);
  });

  const handleSendEmail = () => {
    if (!selectedEvent) {
      setError('Choose an event before sending email.');
      return;
    }

    const recipients = Array.from(
      new Set(
        registrations
          .map((registration) => registration.email)
          .filter(Boolean)
      )
    );

    if (recipients.length === 0) {
      setError('No attendee emails are available for this event.');
      return;
    }

    setError('');
    onSendEmail({
      recipients,
      subject: `RE ${selectedEvent.title}`,
    });
  };

  const handleAddUser = async (
    userIdToAdd = selectedUserId,
    amountOverride = amountPaidToAdd,
    registrationStatusOverride = amountPreset === 'unpaid' ? 'unpaid' : 'registered'
  ) => {
    const targetUserId = Number(userIdToAdd);
    if (!selectedEventId || !targetUserId) {
      setError('Choose an event and a user before adding a registration.');
      return;
    }

    const parsedAmount = Number(amountOverride ?? amountPaidToAdd ?? 0);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError('Amount paid must be a valid non-negative number.');
      return;
    }

    const normalizedStatus = String(registrationStatusOverride || 'registered').trim().toLowerCase();
    const finalStatus = normalizedStatus === 'unpaid' ? 'unpaid' : 'registered';

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${BASE_URL}/${CONTROLLER}`, {
        method: 'POST',
        credentials: 'include', // MANDATORY to attach the PHPSESSID cookie
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: Number(selectedEventId),
          userId: targetUserId,
          amount: parsedAmount,
          amountPaid: parsedAmount,
          amount_paid: parsedAmount,
          paidAmount: parsedAmount,
          paymentAmount: parsedAmount,
          registrationStatus: finalStatus,
          registration_status: finalStatus,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setSelectedUserId('');
      setSelectedUserToAdd(null);
      setUserSearchTerm('');
      setAmountPaidToAdd('');
      setAmountPreset('custom');
      setSuccess('User added to the event.');
      await loadRegistrationsForEvent(selectedEventId);
      await loadCollectedAmounts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRegistration = async (userId) => {
    if (!selectedEventId) return;

    if (!window.confirm('Remove this user from the event?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await fetch(`${BASE_URL}/${CONTROLLER}?eventId=${selectedEventId}&userId=${userId}`, {
        method: 'DELETE',
        credentials: 'include', // MANDATORY to attach the PHPSESSID cookie
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      setSuccess('User removed from the event.');
      await loadRegistrationsForEvent(selectedEventId);
      await loadCollectedAmounts();
    } catch (err) {
      setError(err.message);
    }
  };

  const [activeTab, setActiveTab] = useState('attendees');

  const activeCosts = Array.isArray(selectedEvent?.costs)
    ? selectedEvent.costs
    : Array.isArray(selectedEvent?.eventCosts)
      ? selectedEvent.eventCosts
      : [];

  const normalizedCosts = activeCosts.map((cost) => ({
    category: cost?.category || 'General',
    description: cost?.description || '',
    costType: String(cost?.costType || cost?.type || 'fixed').toLowerCase() === 'variable' ? 'variable' : 'fixed',
    unitAmountChf: Number(cost?.unitAmountChf ?? cost?.unit_amount_chf ?? cost?.amount ?? 0),
  }));

  const validRegistrations = (registrations || []).filter((registration) => {
    const status = String(registration.registration_status || registration.status || '').toLowerCase();
    return status !== 'cancelled' && status !== 'removed';
  });

  const eventRevenue = validRegistrations.reduce((sum, registration) => {
    const amountPaid = Number(registration.amount_paid ?? registration.amountPaid ?? 0);
    return sum + (Number.isFinite(amountPaid) ? amountPaid : 0);
  }, 0);

  const fixedCosts = normalizedCosts
    .filter((cost) => cost.costType !== 'variable')
    .reduce((sum, cost) => sum + Number(cost.unitAmountChf || 0), 0);

  const variableCostPerAttendee = normalizedCosts
    .filter((cost) => cost.costType === 'variable')
    .reduce((sum, cost) => sum + Number(cost.unitAmountChf || 0), 0);

  const attendeeCount = validRegistrations.length;
  const hasExpensesDefined = normalizedCosts.length > 0;
  const variableCostsTotal = attendeeCount * variableCostPerAttendee;
  const contributionPerAttendee = attendeeCount > 0 ? (eventRevenue / attendeeCount) - variableCostPerAttendee : 0;
  const estimatedProfit = eventRevenue - fixedCosts - variableCostsTotal;
  const profitabilityPercentage = eventRevenue > 0 ? (estimatedProfit / eventRevenue) * 100 : 0;
  const profitabilityColor = estimatedProfit < 0 ? '#b42318' : estimatedProfit > 0 ? '#16803c' : '#59636f';
  const attendeesRequiredToBeProfitable = contributionPerAttendee > 0
    ? Math.floor(fixedCosts / contributionPerAttendee) + 1
    : null;

  const chartMaxValue = Math.max(1, eventRevenue, fixedCosts + variableCostsTotal, Math.max(estimatedProfit, 0));

  const tabOptions = [
    { key: 'attendees', label: 'Attendees' },
    { key: 'break-even', label: 'Break Even Analysis' },
  ];

  const renderBreakEvenChart = () => {
    const bars = [
      { label: 'Revenue', value: eventRevenue },
      { label: 'Fixed costs', value: fixedCosts },
      { label: 'Variable costs', value: variableCostsTotal },
      { label: 'Profit', value: estimatedProfit },
    ];

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px', marginTop: '20px' }}>
        {bars.map((bar) => {
          const barHeight = Math.max(12, (Math.abs(bar.value) / chartMaxValue) * 100);
          return (
            <div key={bar.label} style={{ display: 'grid', gap: '8px' }}>
              <div style={{ fontSize: '12px', color: '#59636f', textTransform: 'capitalize' }}>{bar.label}</div>
              <div style={{ height: '120px', border: '1px solid #e1e4e8', borderRadius: '8px', display: 'flex', alignItems: 'flex-end', padding: '8px', background: '#f7f9fb' }}>
                <div
                  style={{
                    width: '100%',
                    height: `${barHeight}%`,
                    minHeight: '12px',
                    background: bar.value >= 0 ? (bar.label === 'Profit' ? '#2e8b57' : '#3b82f6') : '#d9534f',
                    borderRadius: '6px',
                  }}
                />
              </div>
              <div style={{ fontWeight: 600 }}>{formatCHF(bar.value)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Event Registrations</h2>
        {error && <div className="admin-error">{error}</div>}
        {success && <div className="admin-success">{success}</div>}
      </div>

      <div className="admin-registration-layout">
        <aside className="admin-event-list-panel">
          <div className="panel-header">
            <h3>Events</h3>
          </div>

          {events.length === 0 ? (
            <div className="no-records">No events found.</div>
          ) : (
            events.map((event) => {
              const breakeven = getEventBreakeven(event);
              const breakevenColor = breakeven < 0 ? '#b42318' : breakeven > 0 ? '#16803c' : '#59636f';

              return (
                <button
                  key={event.id}
                  type="button"
                  className={`event-select-card ${String(event.id) === String(selectedEventId) ? 'active' : ''}`}
                  onClick={() => setSelectedEventId(String(event.id))}
                >
                  <strong>{event.title}</strong>
                  <span>{event.location || 'Location not set'}</span>
                  <small>{event.startTime ? new Date(event.startTime).toLocaleString() : 'No date'}</small>
                  <span className="event-collected-total">
                    <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span>Collected</span>
                      <strong>{formatCHF(collectedByEvent[String(event.id)] || 0)}</strong>
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'right' }}>
                      <span>Profit</span>
                      <strong style={{ color: breakevenColor }}>{formatCHF(breakeven)}</strong>
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </aside>

        <section className="admin-registration-detail-panel">
          {!selectedEvent ? (
            <div className="no-records">Select an event to manage registrations.</div>
          ) : (
            <>
              <div className="panel-header">
                <h3>{selectedEvent.title}</h3>
              </div>

              <div className="event-registration-meta">
                <span>{selectedEvent.location || 'No location'}</span>
                <span>{selectedEvent.startTime ? new Date(selectedEvent.startTime).toLocaleString() : 'No date'}</span>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '16px 0' }}>
                {tabOptions.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      opacity: activeTab === tab.key ? 1 : 0.75,
                      border: activeTab === tab.key ? '1px solid #d4af37' : '1px solid #d9d9d9',
                      background: activeTab === tab.key ? '#fff9dc' : '#fff',
                    }}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'break-even' ? (
                <div className="admin-form-container" style={{ padding: '18px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    {[
                      { label: 'Revenue', value: eventRevenue },
                      { label: 'Fixed costs', value: fixedCosts },
                      { label: 'Variable costs', value: variableCostsTotal },
                      { label: 'Estimated profit', value: estimatedProfit },
                      { label: 'Profitability', value: profitabilityPercentage, isPercentage: true, color: profitabilityColor },
                    ].map((metric) => (
                      <div key={metric.label} style={{ padding: '12px 14px', border: '1px solid #e1e4e8', borderRadius: '10px', background: '#fafbfc' }}>
                        <div style={{ fontSize: '12px', color: '#59636f' }}>{metric.label}</div>
                        <div style={{ fontSize: '24px', fontWeight: 700, marginTop: '8px', color: metric.color || 'inherit' }}>
                          {metric.isPercentage ? `${metric.value.toFixed(1)}%` : formatCHF(metric.value)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {!hasExpensesDefined && (
                    <div style={{ marginBottom: '20px', padding: '12px 14px', border: '1px solid #b7dfc5', borderRadius: '8px', background: '#effaf2', color: '#176b35' }}>
                      <strong>No expenses have been defined.</strong> All proceeds will be considered profits.
                    </div>
                  )}

                  <table className="data-table" style={{ marginBottom: '20px' }}>
                    <thead>
                      <tr className="table-header-row">
                        <th className="table-header-cell">Metric</th>
                        <th className="table-header-cell">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="table-body-row">
                        <td className="table-body-cell">Attendees registered</td>
                        <td className="table-body-cell">{attendeeCount}</td>
                      </tr>
                      <tr className="table-body-row">
                        <td className="table-body-cell">Variable cost per attendee</td>
                        <td className="table-body-cell">{formatCHF(variableCostPerAttendee)}</td>
                      </tr>
                      <tr className="table-body-row">
                        <td className="table-body-cell">Average revenue per attendee</td>
                        <td className="table-body-cell">{formatCHF(attendeeCount ? eventRevenue / attendeeCount : 0)}</td>
                      </tr>
                      <tr className="table-body-row">
                        <td className="table-body-cell">Attendees required to break even</td>
                        <td className="table-body-cell">
                          {attendeesRequiredToBeProfitable === null
                            ? attendeeCount === 0
                              ? 'Cannot estimate until revenue per attendee is known'
                              : 'Not achievable at current contribution'
                            : `${attendeesRequiredToBeProfitable} ${attendeesRequiredToBeProfitable === 1 ? 'attendee' : 'attendees'}`}
                        </td>
                      </tr>
                      <tr className="table-body-row">
                        <td className="table-body-cell">Contribution per attendee</td>
                        <td className="table-body-cell">{formatCHF(contributionPerAttendee)}</td>
                      </tr>
                    </tbody>
                  </table>

                  {renderBreakEvenChart()}

                  <section style={{ marginTop: '20px' }}>
                    <h4 style={{ margin: '0 0 12px' }}>Cost breakdown</h4>
                    {normalizedCosts.length === 0 ? (
                      <div className="no-records">No costs have been added for this event.</div>
                    ) : (
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr className="table-header-row">
                              <th className="table-header-cell">Cost</th>
                              <th className="table-header-cell">Type</th>
                              <th className="table-header-cell">Amount</th>
                              <th className="table-header-cell">Current total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {normalizedCosts.map((cost, index) => {
                              const currentTotal = cost.costType === 'variable'
                                ? cost.unitAmountChf * attendeeCount
                                : cost.unitAmountChf;

                              return (
                                <tr key={`${cost.category}-${index}`} className="table-body-row">
                                  <td className="table-body-cell">
                                    <strong>{cost.category}</strong>
                                    {cost.description && <div>{cost.description}</div>}
                                  </td>
                                  <td className="table-body-cell">
                                    {cost.costType === 'variable' ? 'Variable' : 'Fixed'}
                                  </td>
                                  <td className="table-body-cell">
                                    {formatCHF(cost.unitAmountChf)}{cost.costType === 'variable' ? ' / attendee' : ''}
                                  </td>
                                  <td className="table-body-cell">{formatCHF(currentTotal)}</td>
                                </tr>
                              );
                            })}
                            <tr className="table-body-row">
                              <td className="table-body-cell" colSpan="3"><strong>Total expenses</strong></td>
                              <td className="table-body-cell"><strong>{formatCHF(fixedCosts + variableCostsTotal)}</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>

                  {hasExpensesDefined && (
                    <div style={{ marginTop: '20px', padding: '12px 14px', border: '1px solid #dfe7ef', borderRadius: '8px', background: '#f4f8fb' }}>
                      <strong>Break-even insight:</strong>{' '}
                      {attendeesRequiredToBeProfitable === null
                        ? attendeeCount === 0
                          ? 'Add paid registrations to estimate the required attendee count.'
                          : 'The event cannot become profitable at the current revenue and variable cost per attendee.'
                        : `You need ${attendeesRequiredToBeProfitable} registered ${attendeesRequiredToBeProfitable === 1 ? 'attendee' : 'attendees'} to cover fixed costs and generate a profit.`}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="admin-form-container registration-add-form">
                    <div className="form-group">
                      <label>Add user to this event</label>
                      <input
                        type="text"
                        placeholder="Search by name or email"
                        value={userSearchTerm}
                        onChange={(e) => {
                          setUserSearchTerm(e.target.value);
                          setSelectedUserId('');
                        }}
                      />
                    </div>

                    <div className="registration-email-actions">
                      <button type="button" className="btn btn-submit-professionals" onClick={handleSendEmail}>
                        Send email to all attendees
                      </button>
                    </div>

                    {userSearchTerm.trim() && (
                      <div className="user-search-results">
                        {availableUsersForEvent.length === 0 ? (
                          <div className="no-records">No matching users available.</div>
                        ) : (
                          availableUsersForEvent.slice(0, 12).map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              className="user-search-result"
                              onClick={() => {
                                setSelectedUserToAdd(user);
                                setSelectedUserId(String(user.id));
                                const memberPrice = Number(selectedEvent?.memberPrice ?? selectedEvent?.member_price ?? 0);
                                const nonMemberPrice = Number(selectedEvent?.nonMemberPrice ?? selectedEvent?.non_member_price ?? 0);
                                setAmountPreset(memberPrice > 0 ? 'member' : 'non-member');
                                setAmountPaidToAdd(String(memberPrice > 0 ? memberPrice : nonMemberPrice));
                              }}
                            >
                              <strong>{user.fullName || 'Unknown user'}</strong>
                              <span>{user.email}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}

                    {selectedUserToAdd && (
                      <div className="admin-form-container" style={{ marginTop: '16px', padding: '16px' }}>
                        <div className="form-group">
                          <label>Selected user</label>
                          <div style={{ fontWeight: 600 }}>{selectedUserToAdd.fullName || 'Unknown user'}</div>
                          <div style={{ opacity: 0.8 }}>{selectedUserToAdd.email}</div>
                        </div>

                        <div className="form-group">
                          <label>Recommended amount</label>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {[
                              { key: 'member', label: 'Member', value: Number(selectedEvent?.memberPrice ?? selectedEvent?.member_price ?? 0) },
                              { key: 'non-member', label: 'Non-member', value: Number(selectedEvent?.nonMemberPrice ?? selectedEvent?.non_member_price ?? 0) },
                              { key: 'unpaid', label: 'Register unpaid', value: 0 },
                            ].map((option) => (
                              <button
                                key={option.key}
                                type="button"
                                className="btn btn-secondary"
                                style={{
                                  minWidth: '132px',
                                  opacity: amountPreset === option.key ? 1 : 0.75,
                                  border: amountPreset === option.key ? '1px solid #d4af37' : '1px solid #d9d9d9',
                                }}
                                onClick={() => {
                                  setAmountPreset(option.key);
                                  setAmountPaidToAdd(String(option.value));
                                }}
                              >
                                {option.label}: {option.value === 0 ? 'CHF 0' : `CHF ${option.value}`}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="custom-amount-paid">Custom amount</label>
                          <input
                            id="custom-amount-paid"
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={amountPaidToAdd}
                            onChange={(e) => {
                              setAmountPreset('custom');
                              setAmountPaidToAdd(e.target.value);
                            }}
                            placeholder="0.00"
                          />
                        </div>

                        <button
                          type="button"
                          className="btn btn-submit-professionals"
                          onClick={() => handleAddUser(selectedUserToAdd.id, amountPaidToAdd, amountPreset === 'unpaid' ? 'unpaid' : 'registered')}
                        >
                          Add to event
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="admin-table-section">
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr className="table-header-row">
                            <th className="table-header-cell">User</th>
                            <th className="table-header-cell">Email</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Amount Paid</th>
                            <th className="table-header-cell">Registered</th>
                            <th className="table-header-cell table-header-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrations.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="table-body-cell">No users registered for this event.</td>
                            </tr>
                          ) : (
                            registrations.map((registration) => (
                              <tr key={registration.id} className="table-body-row">
                                <td className="table-body-cell">{registration.full_name || 'Unknown user'}</td>
                                <td className="table-body-cell">{registration.email || '—'}</td>
                                <td className="table-body-cell">{registration.registration_status || 'unpaid'}</td>
                                <td className="table-body-cell">{formatCHF(registration.amount_paid ?? registration.amountPaid)}</td>
                                <td className="table-body-cell">{registration.registered_at ? new Date(registration.registered_at).toLocaleString() : '—'}</td>
                                <td className="table-body-cell table-body-actions">
                                  <button
                                    type="button"
                                    className="btn-action-delete"
                                    onClick={() => handleDeleteRegistration(registration.user_id)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
